import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, TrendingUp, MapPin, BarChart3, Clock } from 'lucide-react';
import { productAnalyticsAPI, stockAgingAPI } from '../../services/garageApi';
import PriceEvolutionChart from './PriceEvolutionChart';
import StockAgingChart from './StockAgingChart';

const StatCard = ({ icon: Icon, label, value, subtext, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

const ProductAnalytics = ({ productType, productId }) => {
  const [data, setData] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [analyticsResult, agingResult] = await Promise.all([
          productAnalyticsAPI.getByProduct(productType, productId),
          stockAgingAPI.getByProduct(productType, productId).catch(() => null),
        ]);
        setData(analyticsResult);
        setAgingData(agingResult);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [productType, productId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const hasAnalytics = data && !Array.isArray(data) && Object.keys(data).length > 0;
  if (!hasAnalytics) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No analytics data available</p>
        <p className="text-sm mt-1">Data will appear as the product is used in transactions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stock vs Sold + Margin */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          icon={Package}
          label="Total In Stock"
          value={data.total_in_stock ?? 0}
          color="blue"
        />
        <StatCard
          icon={ShoppingCart}
          label="Total Sold"
          value={data.total_sold ?? 0}
          color="green"
        />
      </div>

      {data.price_trend?.current_margin_percent !== undefined && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Cost</p>
            <p className="text-lg font-bold text-orange-600">
              ${data.price_trend.current_cost?.toFixed(2)}
            </p>
          </div>
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Selling</p>
            <p className="text-lg font-bold text-blue-600">
              ${data.price_trend.current_selling?.toFixed(2)}
            </p>
          </div>
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Margin</p>
            <p className="text-lg font-bold text-green-600">
              {data.price_trend.current_margin_percent}%
            </p>
          </div>
        </div>
      )}

      {/* Price Evolution Chart */}
      <div className="bg-white border rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Price Evolution
        </h4>
        <PriceEvolutionChart priceEvolution={data.price_evolution} />
      </div>

      {/* Stock Aging Chart */}
      <div className="bg-white border rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Stock Aging
        </h4>
        <StockAgingChart buckets={agingData} />
      </div>

      {/* Top Locations */}
      <div className="bg-white border rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Top Locations
        </h4>
        {data.top_locations?.length > 0 ? (
          <div className="space-y-2">
            {data.top_locations.map((loc, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' :
                    i === 1 ? 'bg-gray-100 text-gray-700' :
                    'bg-orange-50 text-orange-700'
                  }`}>
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{loc.location_name}</p>
                    <p className="text-xs text-gray-400">{loc.zone_name}</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-700">{loc.quantity} units</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-3">No inventory distribution data</p>
        )}
      </div>
    </div>
  );
};

export default ProductAnalytics;
